import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  ThemeProvider,
  createMuiTheme,
  useTheme,
} from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import useTpvLayout from "src/hooks/useTpvLayout";
import { resolveFontStack } from "src/utils/tpvLayout/defaults";
import palette from "./core/palette";
import shadows, { customShadows } from "./core/shadows";
import typography from "./core/typography";
import breakpoints from "./core/breakpoints";
import borderRadius from "./core/borderRadius";
import componentsOverride from "./core/overrides";

// Sub-ThemeProvider que envuelve SOLO el TPV camarero (TpvCartaLayout). Lee
// la config de apariencia del TPV (themeMode, themeDirection, fontFamily,
// fontScale) y reconstruye un theme anidado por encima del global. Esto
// permite que el restaurante ponga el TPV en oscuro mientras el resto de
// la app (Ajustes, Admin, Login) sigue claro.
//
// Funciona porque MUI soporta nested <ThemeProvider> — el theme interior
// hereda lo no especificado del exterior automáticamente, así que solo
// describimos los campos que CAMBIAN.
//
// La fontScale NO modifica htmlFontSize (eso afectaría a toda la app
// porque htmlFontSize es global del navegador); en su lugar multiplica
// todos los tamaños de la pirámide tipográfica del theme — el efecto es
// idéntico desde el punto de vista del usuario y queda aislado al TPV.

TpvThemeOverride.propTypes = {
  children: PropTypes.node,
  // Fuerza a los hooks de MUI (`useMediaQuery`, `useResponsive`) a creer
  // que el viewport es mobile o desktop, independientemente del ancho real
  // del navegador. Lo usa el panel "Apariencia TPV" para que el preview
  // monte el SliderCartaCamarero en modo mobile aunque el panel esté en
  // un monitor 1920px. Truco: subimos/bajamos los breakpoints del theme
  // a extremos para que `down("sm")` siempre devuelva true (mobile) o
  // false (desktop). MUI evalúa contra `theme.breakpoints` del provider
  // más cercano — funciona sin tocar el window.
  forcedViewport: PropTypes.oneOf(["mobile", "desktop"]),
};

export default function TpvThemeOverride({ children, forcedViewport }) {
  // Antes leíamos el outerTheme y le hacíamos spread; eso provocaba que
  // los `components` y la `typography` del theme global ganaran a los del
  // override (porque `createMuiTheme` con un theme parcial encima de un
  // theme ya completo no hace deep-merge previsible). Resultado: la fuente
  // y los componentes MUI (Button, Typography, etc.) ignoraban la
  // configuración del TPV. La solución es construir el theme DESDE CERO,
  // igual que hace ThemeConfig en `core/index.js:60`.
  const { layout } = useTpvLayout();

  const overriddenTheme = useMemo(() => {
    const isLight = layout.themeMode === "light";

    // Tipografía: parte del global y solo cambia lo que el usuario eligió.
    const fontStack = resolveFontStack(layout.fontFamily);
    const scale = Number(layout.fontScale) || 1;

    // Multiplica recursivamente los `fontSize` numéricos y los strings que
    // expresan rem (ej. "0.875rem"). No toca px directamente porque la
    // pirámide del proyecto usa rem vía pxToRem.
    const scaleFontSize = (size) => {
      if (typeof size === "number") return size * scale;
      if (typeof size === "string") {
        const rem = size.match(/^(\d*\.?\d+)rem$/);
        if (rem) return `${(parseFloat(rem[1]) * scale).toFixed(4)}rem`;
      }
      return size;
    };

    const scaleVariant = (variant) => {
      if (!variant || typeof variant !== "object") return variant;
      const out = { ...variant };
      if ("fontSize" in out) out.fontSize = scaleFontSize(out.fontSize);
      // Los breakpoints responsivos del proyecto inyectan claves @media
      // que también tienen fontSize. Recorrerlas igual.
      for (const k of Object.keys(out)) {
        if (k.startsWith("@media") && out[k]?.fontSize !== undefined) {
          out[k] = { ...out[k], fontSize: scaleFontSize(out[k].fontSize) };
        }
      }
      return out;
    };

    const scaledTypography = {};
    for (const k of Object.keys(typography)) {
      const v = typography[k];
      scaledTypography[k] =
        scale !== 1 && typeof v === "object" ? scaleVariant(v) : v;
    }
    if (fontStack) scaledTypography.fontFamily = fontStack;

    // Paleta y sombras se eligen por themeMode local del TPV (no por el
    // global). Esto es lo que permite TPV oscuro + resto claro.
    const localPalette = palette[isLight ? "light" : "dark"];
    const localShadows = shadows[isLight ? "light" : "dark"];
    const localCustomShadows = isLight
      ? customShadows.light
      : customShadows.dark;

    // Si `forcedViewport` se especifica, deformamos los breakpoints para
    // forzar el resultado de useMediaQuery:
    //   - "mobile":   xs/sm/md/lg/xl muy altos (todo down(*) = true)  →
    //     useResponsive("down","sm") → true ⇒ rama mobile.
    //   - "desktop":  xs/sm/md/lg/xl muy bajos (todo up(*) = true) →
    //     useResponsive("down","sm") → false ⇒ rama desktop.
    // El resto de medidas (typography responsivo) usa breakpoints reales
    // a través de helpers internos, no de aquí, así que no se rompe nada.
    let bp = breakpoints;
    if (forcedViewport === "mobile") {
      bp = { values: { xs: 0, sm: 99999, md: 99999, lg: 99999, xl: 99999 } };
    } else if (forcedViewport === "desktop") {
      bp = { values: { xs: 0, sm: 1, md: 1, lg: 1, xl: 1 } };
    }

    const themeOptions = {
      layout: { contentWidth: 1236 },
      palette: localPalette,
      shadows: localShadows,
      typography: scaledTypography,
      shape: borderRadius,
      breakpoints: bp,
      direction: layout.themeDirection,
      customShadows: localCustomShadows,
      components: componentsOverride({
        theme: {
          palette: localPalette,
          shadows: localShadows,
          typography: scaledTypography,
          shape: borderRadius,
          direction: layout.themeDirection,
        },
      }),
    };

    return createMuiTheme(themeOptions);
  }, [
    layout.themeMode,
    layout.themeDirection,
    layout.fontFamily,
    layout.fontScale,
    forcedViewport,
  ]);

  return (
    <ThemeProvider theme={overriddenTheme}>
      {/* CssBaseline aplica el bg/color del nuevo palette al área TPV. Lo
          inyecta scoped vía MUI; no afecta al resto de la app porque está
          dentro de este ThemeProvider anidado. */}
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
